// Move-and-attack targeting. Pure. No React, no Three.js.
//
// Codex "Move and Attack" + "Range of Motion": a unit may move (one step,
// +Movement N, Airborne diagonals, region transitions, walls block edges)
// and THEN attack from its final cell. A target is legal iff there exists a
// cell the unit can stand on from which the target is within its attack
// envelope (melee = same cell + 4 cardinal; Ranged N = Chebyshev N; sites
// always; subsurface only via a matching region transition).
//
// This is the single source of truth for: highlight, legality recheck, and
// the attacker's final cell (drives the move + region transition before the
// strike). It fixes the bug where targets were offered that the unit could
// not actually reach with its real movement.

import { reachableLocationsInSteps } from './movement';
import { getAttackableCells, getValidAttackTargets } from './combat';
import { isProjectileAttack } from './ranged';

/**
 * @param attacker   cardInstance doing the attacking
 * @param fullCard   catalog card for the attacker (type, etc.)
 * @param from       { col, row, region } current location
 * @param opts       { meshes, cellAt, totalCols, totalRows, getFullCard,
 *                     stepBudget = 1, wallBlocker = null, canTransitionTo = null }
 * @returns Array<{ target, fromCell, projectile, requiresMove, requiresRegionTransition }>
 */
export function getMoveAndAttackTargets(attacker, fullCard, from, opts) {
  const {
    meshes, cellAt, totalCols, totalRows, getFullCard,
    stepBudget = 1, wallBlocker = null, canTransitionTo = null,
    occupiedCells = null,
  } = opts;
  if (!attacker || !from) return [];

  // Cells the attacker already stands on. A multi-cell unit (an
  // animated aura, or any oversized unit) covers several at once and
  // attacks whatever shares ANY of them with no move. Single-cell units
  // just have `from`. Keyed on occupancy, never on card identity, so it
  // generalises to every multi-cell unit.
  const footprintKeys = new Set([`${from.col},${from.row}`]);
  if (Array.isArray(occupiedCells)) {
    for (const c of occupiedCells) footprintKeys.add(`${c.col},${c.row}`);
  }

  // Every location the attacker could be standing on when it attacks,
  // including not moving at all. reachableLocationsInSteps excludes the
  // start, so we add every occupied cell back explicitly (all zero-move).
  const standable = reachableLocationsInSteps(
    attacker, fullCard, from, stepBudget,
    meshes, cellAt, totalCols, totalRows, getFullCard, wallBlocker,
  );
  for (const key of footprintKeys) standable.add(`${key},${from.region}`);

  // Collect the best record per target card id. "Best": attack-in-place
  // over move, then melee over projectile, then fewest steps.
  const best = new Map();
  const consider = (rec) => {
    const id = rec.target.card?.id;
    if (!id) return;
    const prev = best.get(id);
    if (!prev || rank(rec, from) < rank(prev, from)) best.set(id, rec);
  };

  for (const loc of standable) {
    const [colStr, rowStr, region] = loc.split(',');
    const L = { col: parseInt(colStr, 10), row: parseInt(rowStr, 10), region };
    const envelope = getAttackableCells(L.col, L.row, totalCols, totalRows, attacker);
    for (const E of envelope) {
      const targets = getValidAttackTargets(attacker, E.col, E.row, meshes, cellAt, { canTransitionTo });
      for (const t of targets) {
        consider({
          target: t,
          fromCell: L,
          projectile: isProjectileAttack({ col: L.col, row: L.row }, { col: E.col, row: E.row }),
          requiresMove: !footprintKeys.has(`${L.col},${L.row}`),
          requiresRegionTransition: t.requiresRegionTransition || null,
        });
      }
    }
  }
  return [...best.values()];
}

function rank(rec, from) {
  const moveCost = rec.requiresMove ? 1000 : 0;
  const projCost = rec.projectile ? 100 : 0;
  const dist = Math.abs(rec.fromCell.col - from.col) + Math.abs(rec.fromCell.row - from.row);
  return moveCost + projCost + dist;
}
